import { Button, Container, Flex, HStack, Stack, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import i18n from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import localconfig from '../../config';
import { createDropdownTranslatedStaticOptions } from '../../helpers/general';
import { dropdown, simpleInput } from '../../helpers/makeFormFields';
import { manageErrorResponse } from '../../helpers/manageErrorResponse';
import useChainQuery from '../../hooks/useChainQuery';

export interface IChainForm {
  header: string;
  name: string;
  country: string;
  language: string;
}

const ChainForm = (props: { chainId: number }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const axios = useAxios();
  const { chainId } = props;
  const {
    config: { countries = [], languages = [] },
  } = useUserContext();
  const chainQuery = useChainQuery(chainId);
  const method = chainId ? 'put' : 'post';
  const url = `${localconfig.apiBaseUrl}/op/v1/chain${chainId ? `/${chainId}` : ''}`;
  const schema = yup
    .object({
      name: yup.string().required(),
      country: yup.string().required(),
      language: yup.string().required(),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    control,
    register,
    setError,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate } = useMutation<any, Error, IChainForm>({
    mutationFn: async (data) => {
      // @ts-ignore
      const response = await axios[method](
        url,
        data,
        // @ts-ignore
      ).catch((err) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      });

      return response;
    },
  });
  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = (data) => {
    // @ts-expect-error
    mutate(data, {
      onSuccess: ({ data }) => {
        navigate(`/app/op/chains/${data.id}`);
      },
      onError: (error: any) => {
        const { errors } = error.response.data;

        (Object.keys(errors) as Array<keyof IChainForm>).forEach((key) => {
          // @ts-expect-error
          setError(key, {
            type: 'server',
            message: errors[key]!.join('. '),
          });
        });
      },
    });
  };

  const country = watch('country');
  const getLanguageDropdownOptions = (country: string) => {
    if (!country) {
      return [];
    }
    return createDropdownTranslatedStaticOptions({
      //@ts-ignore
      records: languages[country],
      translationKey: `location:languages`,
      initialOptions: { value: '', label: i18n.t('common:choose') },
    });
  };
  useEffect(() => {
    reset({
      ...(chainQuery.data || {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainQuery.isFetching]);

  useEffect(() => {
    if (country) {
      //@ts-ignore
      setValue('language', languages[country][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);
  if (chainQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <Stack w="full">
      <Container as="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack px={{ base: '4', md: '6' }} py={{ base: '5', md: '6' }}>
          <Stack direction={['column']}>
            {simpleInput({
              name: 'name',
              label: t('forms:fullname.label'),
              register,
              errors,
            })}
            {dropdown({
              name: 'country',
              label: t('forms:country.label'),
              // @ts-ignore
              control,
              errors,
              options: createDropdownTranslatedStaticOptions({
                records: countries,
                translationKey: 'location:countries',
                initialOptions: { value: '', label: i18n.t('common:choose') },
              }),
            })}
            {dropdown({
              name: 'language',
              label: t('workshop:language.label'),
              // @ts-ignore
              control,
              errors,
              options: getLanguageDropdownOptions(country),
            })}
          </Stack>

          <Flex direction="row-reverse" py="4" px={{ base: '4', md: '6' }}>
            <HStack>
              <Button
                color={'red.600'}
                borderColor={'red.600'}
                //@ts-ignore
                _hover={'pink.600'}
                variant="outline"
                onClick={() => {
                  navigate(`/app/op/chains`);
                }}
              >
                {t('common:abort')}
              </Button>

              <Button type="submit" variant="primary">
                {t(isEmpty(chainQuery.data) ? 'common:create' : 'common:update')}
              </Button>
            </HStack>
          </Flex>
        </Stack>
      </Container>
    </Stack>
  );
};
export default ChainForm;
